/**
 * **`legacy/frontend/src/screens/WhenField.tsx` whole, not a fork of it.**
 *
 * The file arrived in two commits of one slice, because the arithmetic under it is pure
 * and is where every visible date in F-005 comes from: T4 brought the module scope and
 * tested it, T5 put `WhenField` itself (legacy `:64-209`) and the `sameDay` only it reads
 * (`:386-392`) back where they belong, and changed nothing T4 had written. Nothing is
 * absent now; what follows is the whole of the old file, in its own order.
 *
 * **Three lines differ from the old file, and every one of them is an import.**
 * `SessionKind` comes from `@contracts/index` rather than from `../api`, because a server
 * shape is defined in the frozen contract package and nowhere else — ADR-0011, the same
 * split `format.ts` and `BirthdayCard.tsx` carry at their own first import — and that
 * forces the blank line the type import stands on. The react hooks and the four relative
 * imports below it are the old file's, in the old file's order.
 *
 * **Six declarations gain the word `export`** — `HOURS`, `MINUTES`, `suggestions`,
 * `nextBookable`, `daysFrom` and `clamp` — and that word is the only thing in this file
 * that is neither the old app's nor a comment saying so. It was needed while `WhenField`
 * was another commit's: `noUnusedLocals` (`tsconfig.app.json`) would have refused all six
 * and nothing could have asserted AC-29. It stays now the component is back, because the
 * unit tests behind AC-16…AC-22 and AC-29 import all six by name and removing the word
 * would be a change made to the port for no reason the old app has. The six lines
 * therefore sit **outside** every region claimed in `scripts/ported-partial.txt`; the
 * comments above them are inside one.
 *
 * `MONTHS_AHEAD` stays this file's own second copy of `TimeWheel.tsx`'s 18, as it is over
 * there (`:39-40`).
 *
 * **Two lines below look like slips and are not**, and they are noted here rather than
 * beside themselves so that the ported region stays byte-identical to its source:
 *
 *   * «Другая дата»'s handler is `setOpened(!open)` and **not** `setOpened(!opened)`
 *     (`:164-174`). The two are equivalent only because the button is not drawn at all
 *     when `offers` is empty, which is the one state where `open` and `opened` disagree —
 *     so there is nothing to close and nothing to change. Spec §1.4 says «port the line as
 *     written»; rewriting it to `!opened` would be a deviation with no visible difference,
 *     which is the worst kind to make.
 *   * `chosen` is a **ref** and not state (`:79-84`), so the field going back to following
 *     its offer never re-renders on its own. That is deliberate: what the person sees
 *     changes only when the value does, and the value change is the form's to report.
 *     `chosen.current = offer.value !== offers[0].value` (`:123`) is the whole of the rule
 *     AC-24 pins — tapping row 1 is the way back to following the suggestion.
 *
 * `FND-074` is `KEEP`: reopening the sheet inside its closing animation keeps the last
 * opening's wheel, because `opened` survives with the mount. No `key` forces a remount
 * here — that would be the fix the finding declined.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import type { SessionKind } from '@contracts/index';

import {
  formatLongDate,
  formatTime,
  formatWeekdayDate,
  formatWheelDate,
  plural,
  toLocalInputValue,
  weekdayShort,
  words,
} from '../format';
import { CheckIcon, ChevronDownIcon } from '../icons';
import { WheelBox } from './TimeWheel';
import { Column } from './Wheel';
// C-016 (FND-1611): the fold the wheel opens and closes through behind «Другая дата».
import { FoldWhere } from './Unfold';

/** A gathering the choir already knows about: what it was, and when it began. */
export type Held = { kind: SessionKind; start: string };

/** One of the two dates offered before anybody has to pick one. */
type Offer = {
  /** The local value the form carries, and what tells the two rows apart. */
  value: string;
  /** «чт 27 августа, 19:00». */
  said: string;
  /** Why this date is offered, or null on the row that states its distance. */
  note: string | null;
  /** «через 2 нед.», on the right of the second row. */
  far: string | null;
};

/** How many of the choir's own gatherings a habit is read off. */
const SAMPLE = 8;

/** The minutes column goes in fives: nobody schedules a rehearsal for 19:23. */
const STEP = 5;

/** How far ahead the date column runs. Past that a choir is not planning. */
const MONTHS_AHEAD = 18;

const SUNDAY = 0;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * «Когда»: the date the choir keeps anyway, and a wheel for the rest.
 *
 * It was one `input[type=datetime-local]` and the browser's own dialogue
 * behind it. What that asked for was a date out of the whole calendar, and the
 * whole calendar is not the question: a choir rehearses on the same weekday at
 * the same hour, so nine times out of ten scheduling one is confirming a date
 * everybody already knows. That should cost one tap, and it does — the first
 * row is chosen when the form opens, and «Сохранить» is the second tap.
 *
 * A month calendar was the other candidate and lost twice: it takes half the
 * screen, and it would put a second language of picking beside the wheel the
 * birthday already uses.
 *
 * The suggestions are read off the schedule and the history the screen has
 * already loaded — the server is asked for nothing new — and they are never a
 * claim about a habit the choir has not got: a choir with one gathering behind
 * it is told «как в прошлый раз», and one with none is offered no row at all.
 */
export default function WhenField({
  kind,
  held,
  value,
  onChange,
}: {
  /** Which of the two the form is filling in: they are different habits. */
  kind: SessionKind;
  held: Held[];
  /** The same local `YYYY-MM-DDTHH:mm` the field used to carry. */
  value: string;
  onChange: (value: string) => void;
}) {
  const offers = useMemo(() => suggestions(kind, held), [kind, held]);
  const [opened, setOpened] = useState(false);
  /**
   * Whether a date has been chosen by hand. Until one has, the field follows
   * the suggestion: the history arrives after the form is drawn, and the
   * segment above it can change what a habit even is.
   */
  const chosen = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (chosen.current || offers.length === 0) return;
    onChangeRef.current(offers[0].value);
  }, [offers]);

  // Nothing to suggest is exactly when the wheel is needed, so it opens with
  // the form rather than behind a button naming an alternative to nothing.
  const open = opened || offers.length === 0;

  // The first bookable moment, read once: recomputing it while a finger is on
  // the wheel would move the columns under it.
  const floor = useMemo(nextBookable, []);
  const days = useMemo(() => daysFrom(floor), [floor]);
  const dayLabels = useMemo(() => days.map(formatWheelDate), [days]);

  const when = new Date(value);
  const today = sameDay(when, floor);
  // A date today cannot be one that has passed, so the columns start where the
  // clock is - the same clamping the birthday's day column does by month.
  const firstHour = today ? floor.getHours() : 0;
  const firstMinute = today && when.getHours() === floor.getHours() ? floor.getMinutes() : 0;

  function set(day: Date, hour: number, minute: number): void {
    const picked = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute);
    const next = toLocalInputValue(picked.getTime() < floor.getTime() ? floor : picked);
    // The wheel reports its value again after every scroll it is sent on, and
    // being put where it already stands is not a choice anybody made.
    if (next === value) return;
    chosen.current = true;
    onChange(next);
  }

  function choose(offer: Offer): void {
    // The first row is the way back to following the suggestion; the second is
    // a date of its own.
    chosen.current = offer.value !== offers[0].value;
    onChange(offer.value);
  }

  return (
    <div className="field">
      <span className="when-head">
        Когда
        {/* Where the birthday's value stands: beside the label, in the face
            figures are set in. It appears with the wheel, because until then
            the chosen row says it in words. */}
        {open && (
          <span className="when-value">
            {formatWeekdayDate(value)} · {formatTime(value)}
          </span>
        )}
      </span>

      {offers.length > 0 && (
        <div className="when-offers">
          {offers.map((offer) => (
            <button
              key={offer.value}
              className={offer.value === value ? 'when-offer when-offer--picked' : 'when-offer'}
              aria-pressed={offer.value === value}
              onClick={() => choose(offer)}
            >
              {offer.value === value && <CheckIcon size={18} />}
              <span className="when-offer-body">
                <span className="when-offer-date">{offer.said}</span>
                {offer.note !== null && <span className="when-offer-note">{offer.note}</span>}
              </span>
              {offer.far !== null && <span className="when-offer-far">{offer.far}</span>}
            </button>
          ))}
        </div>
      )}

      {/* The suggestions stay while the wheel is open, and the first of them
          still returns an ordinary date in one tap: otherwise this button is a
          road with no way back. */}
      {offers.length > 0 && (
        <button
          className={open ? 'btn btn--outline when-more when-more--open' : 'btn btn--outline'}
          onClick={() => setOpened(!open)}
        >
          Другая дата
          {/* Turned rather than swapped: the button survives the fold, so its
              chevron is the same element on both sides of it. */}
          <ChevronDownIcon size={16} turned={open} />
        </button>
      )}

      {/* C-016 (FND-1611): behind «Другая дата» the wheel opens and closes through
          `Unfold`, 220ms in and 180ms out, instead of arriving whole in one frame and
          jumping the sheet's top edge by its 170px. With nothing offered there is no
          button, the wheel is simply part of the form, and it stands as it did. */}
      <FoldWhere fold={offers.length > 0} open={open}>
        <WheelBox modifier="wheel--when">
          {/* Real future dates rather than a number and a month picked apart:
              31 February and last year are not in the column at all, so the
              server's refusal stays a guard rather than something anybody
              meets. It is also why there is no year column. */}
          <Column
            label="Когда: дата"
            items={dayLabels}
            index={clamp(daysBetween(floor, when), days.length)}
            onPick={(index) => set(days[index], when.getHours(), when.getMinutes())}
          />
          <Column
            label="Когда: час"
            items={HOURS.slice(firstHour)}
            index={clamp(when.getHours() - firstHour, HOURS.length - firstHour)}
            onPick={(index) => set(when, firstHour + index, when.getMinutes())}
            mono
          />
          <Column
            label="Когда: минуты"
            items={MINUTES.slice(firstMinute / STEP)}
            index={clamp(
              Math.floor((when.getMinutes() - firstMinute) / STEP),
              MINUTES.length - firstMinute / STEP,
            )}
            onPick={(index) => set(when, when.getHours(), firstMinute + index * STEP)}
            mono
          />
        </WheelBox>
      </FoldWhere>
    </div>
  );
}

export const HOURS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
export const MINUTES = Array.from({ length: 60 / STEP }, (_, step) =>
  String(step * STEP).padStart(2, '0'),
);

/**
 * The two dates the form offers, or none when the choir has no habit to read.
 *
 * The second is the first plus a week: a regent who is booking ahead is booking
 * the next one of the same, and that is one tap rather than a wheel.
 */
export function suggestions(kind: SessionKind, held: Held[]): Offer[] {
  const taken = booked(held);
  const sample = recent(kind, held);
  const habit = shape(sample);
  if (habit !== null) {
    return pair(next(habit.weekday, habit.minutes), spoken(kind, sample.length), taken);
  }
  // The choir's first service, with rehearsals already behind it. A service is
  // on a Sunday and at the hour the choir gathers, and the row says that rather
  // than «как всегда»: there is no «always» yet.
  if (kind !== 'service') return [];
  const rehearsals = shape(recent('rehearsal', held));
  if (rehearsals === null) return [];
  return pair(next(SUNDAY, rehearsals.minutes), 'ближайшее воскресенье', taken);
}

/**
 * The days the choir has already got something on, of either kind.
 *
 * Without this the habit offers the evening it was itself read off: a rehearsal
 * booked for next Thursday makes next Thursday the commonest day there is, and
 * the two taps the form promises would end on «Это время уже занято». The
 * server refuses anything overlapping a gathering's own hours whatever its
 * kind, so a day is the coarse shape of that rule and the wheel is the way past
 * it — the arithmetic itself stays on the server, where it is decided.
 */
function booked(held: Held[]): Set<string> {
  const now = Date.now();
  return new Set(
    held
      .map((one) => new Date(one.start))
      .filter((when) => when.getTime() > now)
      .map(dayKey),
  );
}

function dayKey(when: Date): string {
  return `${when.getFullYear()}-${when.getMonth()}-${when.getDate()}`;
}

/** Up to eight of the choir's most recent gatherings of one kind, newest first. */
function recent(kind: SessionKind, held: Held[]): Date[] {
  return held
    .filter((one) => one.kind === kind)
    .map((one) => new Date(one.start))
    .sort((one, other) => other.getTime() - one.getTime())
    .slice(0, SAMPLE);
}

/** The weekday and the time this choir keeps, or null when there is no sample. */
function shape(sample: Date[]): { weekday: number; minutes: number } | null {
  if (sample.length === 0) return null;
  return {
    weekday: commonest(sample.map((when) => when.getDay())),
    minutes: commonest(sample.map(minutes)),
  };
}

/**
 * The commonest of the values, and the most recent of them where two tie.
 *
 * The list arrives newest first, so first-past-the-post over it answers a choir
 * that has just moved its rehearsal with the new day rather than the old one.
 */
function commonest(values: number[]): number {
  const counts = new Map<number, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  let best = values[0];
  for (const value of values) {
    if ((counts.get(value) ?? 0) > (counts.get(best) ?? 0)) best = value;
  }
  return best;
}

function minutes(when: Date): number {
  return when.getHours() * 60 + when.getMinutes();
}

/** That weekday at that time, the next time it comes round, and never now. */
function next(weekday: number, minutesOfDay: number): Date {
  const now = new Date();
  const when = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, minutesOfDay);
  when.setDate(when.getDate() + ((weekday - when.getDay() + 7) % 7));
  if (when.getTime() <= now.getTime()) when.setDate(when.getDate() + 7);
  return when;
}

function pair(first: Date, note: string, taken: Set<string>): Offer[] {
  const one = free(first, taken);
  const two = free(week(one), taken);
  return [
    { value: toLocalInputValue(one), said: said(one), note, far: null },
    { value: toLocalInputValue(two), said: said(two), note: null, far: weeksAway(two) },
  ];
}

/** The same day of the week, a week on. */
function week(when: Date): Date {
  const later = new Date(when);
  later.setDate(later.getDate() + 7);
  return later;
}

/** That date, or the first week after it the choir has nothing on. */
function free(when: Date, taken: Set<string>): Date {
  let free = when;
  while (taken.has(dayKey(free))) free = week(free);
  return free;
}

/** «чт 27 августа, 19:00» — a date to be read, not compared with the one above. */
function said(when: Date): string {
  const iso = when.toISOString();
  return `${weekdayShort(when)} ${formatLongDate(iso)}, ${formatTime(iso)}`;
}

/**
 * Why the date is offered, in words that never claim a habit the choir has not
 * got. Three cases and not one template: «как последние две спевки» is a
 * sentence about a pattern, and two gatherings are not one.
 */
function spoken(kind: SessionKind, count: number): string {
  if (count < 3) return 'как в прошлый раз';
  return `как последние ${NUMERALS[count]} ${plural(count, words(kind).counted)}`;
}

/** Three to eight in words: the sample is never larger and never smaller here. */
const NUMERALS: Record<number, string> = {
  3: 'три',
  4: 'четыре',
  5: 'пять',
  6: 'шесть',
  7: 'семь',
  8: 'восемь',
};

/** «через 2 нед.» — the distance the schedule's own rows state in days. */
function weeksAway(when: Date): string {
  return `через ${Math.ceil(daysBetween(new Date(), when) / 7)} нед.`;
}

/** The nearest moment the wheel can name: now, up to the next five minutes. */
export function nextBookable(): Date {
  const when = new Date();
  when.setSeconds(0, 0);
  // The schedule refuses its own past, so the current minute is already gone by
  // the time the request lands.
  when.setMinutes(Math.ceil((when.getMinutes() + 1) / STEP) * STEP);
  return when;
}

/** Every day from the first bookable one to eighteen months past it. */
export function daysFrom(floor: Date): Date[] {
  const day = new Date(floor.getFullYear(), floor.getMonth(), floor.getDate());
  const last = new Date(day);
  last.setMonth(last.getMonth() + MONTHS_AHEAD);
  const all: Date[] = [];
  while (day.getTime() <= last.getTime()) {
    all.push(new Date(day));
    day.setDate(day.getDate() + 1);
  }
  return all;
}

function sameDay(one: Date, other: Date): boolean {
  return (
    one.getFullYear() === other.getFullYear() &&
    one.getMonth() === other.getMonth() &&
    one.getDate() === other.getDate()
  );
}

/**
 * Whole days between two moments, by the calendar rather than by the clock: an
 * hour lost to daylight saving must not make a day count as none.
 */
function daysBetween(from: Date, to: Date): number {
  const startOfDay = (when: Date): number =>
    new Date(when.getFullYear(), when.getMonth(), when.getDate()).getTime();
  return Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS);
}

export function clamp(index: number, length: number): number {
  return Math.max(0, Math.min(length - 1, index));
}
